import {readable, writable} from "svelte/store";

export const requestCache = new Map();

export function areaQueryURL(report, layerId) {
  const url = new URL(ENVIRONMENT.baseURL());
  url.pathname = `/GeoFeature/areaQuery/${report.subject.id}`;
  url.searchParams.set('layerIds', layerId);
  return url.toString();
}

export function areaAggregateQuery (report, layerId) {
  if (report) {
    const url = areaQueryURL(report, layerId);
    return cachedRequest(url);
  }
  return {
    request: Promise.reject(),
    retryStore: writable(undefined)
  };
}

export function geoFeature (report) {
  if (report && report.subject && report.subject.id) {
    const url = new URL(ENVIRONMENT.baseURL());
    url.pathname = `/GeoFeature/byId/${report.subject.id}`;
    return cachedRequest(url);
  }

  return {
    request: Promise.reject(),
    retryStore: writable(undefined)
  };
}

export function geoFeatureArea (report) {
  if (report) {
      const url = new URL(ENVIRONMENT.baseURL());
      url.pathname = `/GeoFeature/area/${report.subject.id}`;
      return cachedRequest(url);
  }

  return {
    request: Promise.reject(),
    retryStore: writable(undefined)
  };
}

export function cachedRequest (url, placeholderResponse) {
  const retryStore = writable(undefined);

  const retries = () => {
    return new Promise((resolve, reject) => {
      retryStore.set(() => {
        requestCache.delete(url);
        retryStore.set(undefined);
        resolve();
      });
    });
  };

  const request = async function*() {
    while (true) {
      if (!requestCache.has(url)) {
        requestCache.set(url, fetch(url)
            .then(r => {
              if (r.ok) {
                return r
              } else {
                const error = new Error(r.statusText);
                error.response = r;
                return Promise.reject(error);
              }
            })
            .then(r => r.json()))
      }

      try {
        const response = await requestCache.get(url);
        yield response; // success!
        break; // stop looping!
      } catch (e) {
        const requestedRetry = retries();
        yield e;
        await requestedRetry;
      }
    }
  };

  return {
    request,
    retryStore
  }
}

/**
 * Wraps an async generator function `async function* () {...}` in a svelte store.
 *
 * See the store contract https://svelte.dev/docs#Store_contract
 *
 * @param asyncGen
 * @param initialValue
 * @returns {Readable<undefined>}
 */
export function asyncGenToStore(asyncGen, initialValue = undefined) {

  if (!asyncGen) {
    return readable(undefined, () => {});
  } else if (typeof asyncGen !== 'function') {
    return readable(undefined, set => {
      Promise.resolve(asyncGen).then(result => {
        set(result);
      })
    });
  }

  let subscriptions = new Set();

  let currentValue = initialValue;

  let start;

  if (typeof asyncGen === 'function') {
    start = async () => {
      for await (let a of asyncGen()) {
        try {
          const d = await a;
          if (!(d instanceof Error)) {
            currentValue = d;
            [...subscriptions].forEach(s => s(d));
          }
        } catch (e) {

        }
      }
    }
  }

  let started = false

  return {
    subscribe(subscription) {
      subscriptions.add(subscription);
      subscription(currentValue);
      if (!started) {
        started = true;
        start();
      }

      return () => {
        subscriptions.delete(subscription);
      }
    }
  }
}
